import { backdropFor, hero, persist, toHub } from "../app.ts";
import { sfx } from "../audio.ts";
import { itemsFor } from "../data/items.ts";
import { now, setScene } from "../engine.ts";
import { Actor } from "../render/actor.ts";
import { drawArena, GROUND, W } from "../render/arena.ts";
import { derive, equip, meetsReq, sellPrice, STAT_INFO } from "../rules.ts";
import { SLOTS, STAT_KEYS, type Gear, type Item, type Slot } from "../types.ts";
import { btn, coin, h, mount, toast } from "../ui/dom.ts";
import { muteButton } from "./common.ts";

const TAB_NAMES: Record<Slot, string> = { weapon: "Weapons", shield: "Shields", helmet: "Helmets", armor: "Armor", boots: "Boots" };

function statLine(it: Item): HTMLElement {
  const parts: string[] = [];
  if (it.slot === "weapon") {
    parts.push(`⚔️ ${it.min}–${it.max} dmg`);
    if (it.acc) parts.push(`🎯 ${it.acc > 0 ? "+" : ""}${it.acc}% hit`);
    if (it.reach === 2) parts.push("📏 Reach 2");
    if (it.twoHanded) parts.push("✋✋ Two-handed");
  } else {
    if (it.slot === "shield") parts.push(`🛡️ ${it.block}% block`);
    if (it.armor) parts.push(`🧱 +${it.armor} armor`);
  }
  for (const k of STAT_KEYS) {
    const b = it.bonus?.[k];
    if (b) parts.push(`${b > 0 ? "+" : ""}${b} ${STAT_INFO[k].name.slice(0, 3).toUpperCase()}`);
  }
  return h("div", { class: "st" }, ...parts.map((p) => h("span", null, p)));
}

function reqText(it: Item): string {
  return STAT_KEYS.filter((k) => it.req?.[k])
    .map((k) => `${STAT_INFO[k].name} ${it.req?.[k]}`)
    .join(", ");
}

export function armoryScreen(): void {
  const me = hero();
  const actor = new Actor(me.look, me.gear, 1, 300);
  const backdrop = backdropFor(me);
  setScene((ctx, t) => {
    drawArena(ctx, backdrop, t, 0.1);
    ctx.fillStyle = "rgba(15,8,4,0.45)";
    ctx.fillRect(0, 0, W, 720);
    actor.draw(ctx, t, GROUND - 80, 2.6);
  });

  let tab: Slot = "weapon";
  const goldEl = h("div", { class: "gold" });
  const tabs = h("div", { class: "tabs" });
  const list = h("div", { class: "items" });
  const derivedEl = h("div", { class: "gearlist panel" });

  function previewWith(id: string | null): void {
    if (!id) return actor.setGear(me.gear);
    const trial = { ...me, gear: { ...me.gear } as Gear };
    equip(trial, id);
    actor.setGear(trial.gear);
  }

  function render(): void {
    goldEl.replaceChildren(coin(), String(me.gold));
    tabs.replaceChildren(
      ...SLOTS.map((s) =>
        h("button", {
          class: `tab${s === tab ? " on" : ""}`,
          onclick: () => {
            tab = s;
            sfx.click();
            render();
          },
        }, TAB_NAMES[s]),
      ),
    );
    list.replaceChildren(
      ...itemsFor(tab)
        .filter((it) => it.price > 0 || me.inventory.includes(it.id))
        .map((it) => {
          const owned = me.inventory.includes(it.id);
          const equipped = me.gear[it.slot] === it.id;
          const canUse = meetsReq(me, it);
          const acts: HTMLElement[] = [];
          if (!owned) {
            acts.push(h("div", { class: "price" }, coin(), it.price));
            acts.push(
              btn("Buy", () => {
                me.gold -= it.price;
                me.inventory.push(it.id);
                if (canUse) equip(me, it.id);
                sfx.coin();
                toast(`Bought ${it.name}!`);
                done();
              }, "small green", { disabled: me.gold < it.price || !canUse }),
            );
          } else {
            if (equipped) {
              acts.push(btn("Unequip", () => {
                delete me.gear[it.slot];
                done();
              }, "small wood"));
            } else {
              acts.push(btn("Equip", () => {
                equip(me, it.id);
                done();
              }, "small blue", { disabled: !canUse }));
            }
            if (it.price > 0) {
              acts.push(btn(`Sell ${sellPrice(it)}`, () => {
                me.inventory = me.inventory.filter((x) => x !== it.id);
                if (me.gear[it.slot] === it.id) delete me.gear[it.slot];
                me.gold += sellPrice(it);
                sfx.coin();
                done();
              }, "small"));
            }
          }
          return h(
            "div",
            {
              class: `item${equipped ? " equipped" : ""}`,
              onmouseenter: () => previewWith(it.id),
              onmouseleave: () => previewWith(null),
            },
            h(
              "div",
              null,
              h("div", { class: "nm" }, it.name, equipped ? " ✔" : ""),
              statLine(it),
              h("div", { class: "ds" }, it.desc),
              it.req && !canUse && h("div", { class: "req" }, `Requires ${reqText(it)}`),
            ),
            h("div", { class: "acts" }, ...acts),
          );
        }),
    );
    const d = derive(me.base, me.gear);
    derivedEl.replaceChildren(
      h("h3", null, "Battle Readiness"),
      h(
        "div",
        { class: "derived" },
        h("span", null, `❤️ ${d.maxHp} HP`),
        h("span", null, `⚡ ${d.maxEn} EN`),
        h("span", null, `⚔️ ${d.weapon.name} ${d.weapon.min}–${d.weapon.max}`),
        h("span", null, `🧱 Armor ${d.armor}`),
        h("span", null, `🛡️ Block ${Math.round(d.block)}%`),
      ),
    );
  }

  function done(): void {
    persist();
    actor.setGear(me.gear);
    void actor.play("taunt", now());
    render();
  }

  render();
  mount(
    (e) => e.key === "Escape" && toHub(),
    h(
      "div",
      { class: "armory panel" },
      h("div", { class: "row" }, h("h2", { class: "grow" }, "The Armory"), goldEl),
      tabs,
      list,
      h("div", { class: "row", style: { justifyContent: "flex-end", marginTop: "10px" } }, btn("◀ Back to the Roost", toHub, "wood")),
    ),
    derivedEl,
    muteButton(),
  );
}
